import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Briefcase,
  Calendar,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  FileText,
  HelpCircle,
  Lock,
  MessageSquare,
  PlayCircle,
  Plus,
  RefreshCw,
  User,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import CandidateShell from '@/components/candidate/CandidateShell';
import RaiseTicketModal from '@/components/candidate/RaiseTicketModal';
import CandidateTicketThread from '@/components/candidate/CandidateTicketThread';
import DashboardKpiCard from '@/components/ux/DashboardKpiCard';
import StatusBadge from '@/components/ux/StatusBadge';
import EmptyState from '@/components/ux/EmptyState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import Pagination from '@/components/ui/Pagination';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const TICKET_STATUS_STYLES = {
  open:        'bg-blue-500/10 text-blue-600 border-blue-500/20',
  in_progress: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  resolved:    'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
  closed:      'bg-slate-500/10 text-slate-500 border-slate-400/20',
};
const TICKET_STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };

const FILTER_TABS = [
  { value: 'all', label: 'All' },
  { value: 'ready', label: 'Ready' },
  { value: 'completed', label: 'Done' },
  { value: 'closed', label: 'Closed' },
];

/**
 * Whether the link window has closed.
 *
 * The server sends the window, because the link is valid across a range rather
 * than dying on the scheduled time. Deriving it here from scheduledAt alone
 * showed "Link expired" minutes after a slot the server was still serving.
 * Sessions from before the server sent a window fall back to trusting status.
 */
function isSessionExpired(session) {
  if (!session?.linkClosesAt) return false;
  return new Date() > new Date(session.linkClosesAt);
}

/** The link is not open yet — the candidate is early, not locked out. */
function isSessionNotYetOpen(session) {
  if (!session?.linkOpensAt) return false;
  return new Date() < new Date(session.linkOpensAt);
}

function resolveSessionStatus(session) {
  // Prefer DB-persisted status; fall back to client-side expiry check for 'scheduled'
  if (session.status === 'expired') return 'expired';
  if (session.status === 'cancelled') return 'cancelled';
  if (session.status === 'terminated') return 'terminated';
  if (session.status === 'completed') return 'completed';
  if (session.status === 'disconnected') return 'disconnected';
  if (session.status === 'in_progress') return 'in_progress';
  // Client-side safety net: treat stale 'scheduled' as expired before the server catches it
  if (session.status === 'scheduled' && isSessionExpired(session)) return 'expired';
  return session.status || 'scheduled';
}

function canStartInterview(session) {
  return session.status === 'scheduled' &&
    !isSessionExpired(session) &&
    !isSessionNotYetOpen(session) &&
    !session.locked;
}

function canResumeInterview(session) {
  return session.status === 'in_progress' || session.status === 'disconnected';
}

function formatDuration(minutes) {
  if (!minutes) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

/** "2 Sept, 1:52 am" — short and unambiguous for a candidate. */
function formatWhen(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString([], {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/** "in 2 hours", "in 14 minutes" — how long until the link opens. */
function formatCountdown(iso) {
  const ms = new Date(iso) - new Date();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${mins} minute${mins === 1 ? '' : 's'}`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs} hour${hrs === 1 ? '' : 's'}`;
  const days = Math.round(hrs / 24);
  return `in ${days} day${days === 1 ? '' : 's'}`;
}

/**
 * The actual scheduled date and time, always.
 *
 * This used to print "Today 03:52" / "Tomorrow 03:52". Relative wording reads
 * fine until it is wrong — an interview 23 hours away was called "Today" — and
 * even when correct it makes a candidate work out the real date. A booking
 * should state its date plainly, with the relative day as a hint beside it.
 */
function formatScheduledOn(scheduledAt) {
  if (!scheduledAt) return '';
  return new Date(scheduledAt).toLocaleString([], {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** "Today", "Tomorrow", "in 3 days" — context, never the whole answer. */
function relativeDayHint(scheduledAt) {
  if (!scheduledAt) return null;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const delta = Math.round((startOfDay(new Date(scheduledAt)) - startOfDay(new Date())) / 86400000);
  if (delta === 0)  return 'Today';
  if (delta === 1)  return 'Tomorrow';
  if (delta === -1) return 'Yesterday';
  if (delta > 1)    return `in ${delta} days`;
  return `${Math.abs(delta)} days ago`;
}

function getFilterBucket(session) {
  const status = resolveSessionStatus(session);
  if (canStartInterview(session)) return 'ready';
  if (canResumeInterview(session)) return 'ready'; // disconnected/in_progress also appear in ready tab
  if (status === 'completed') return 'completed';
  // expired, cancelled, terminated, locked-scheduled → all map to 'closed'
  return 'closed';
}

function SessionAction({ session, onStart }) {
  const ready = canStartInterview(session);
  const cancelled = session.status === 'cancelled';
  const canResume = canResumeInterview(session);

  // Round locked — previous round not yet passed
  if (session.locked && session.status === 'scheduled') {
    const blockingRound = session.prevRoundName || 'the previous round';
    return (
      <span
        title={`Complete "${blockingRound}" first`}
        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400"
      >
        <Lock className="h-3 w-3" />
        {blockingRound} pending
      </span>
    );
  }

  // Disconnected or stuck in_progress — show reconnect button
  if (canResume) {
    return (
      <Button
        size="sm"
        className="h-8 shrink-0 gap-1.5 px-3 text-xs bg-amber-600 hover:bg-amber-500 text-white"
        onClick={() => onStart(session.uniqueLink)}
      >
        <RefreshCw className="h-3.5 w-3.5" />
        {session.status === 'disconnected' ? 'Reconnect' : 'Resume'}
      </Button>
    );
  }

  if (ready) {
    return (
      <Button size="sm" className="h-8 shrink-0 gap-1.5 px-3 text-xs" onClick={() => onStart(session.uniqueLink)}>
        <PlayCircle className="h-3.5 w-3.5" />
        Start interview
      </Button>
    );
  }
  if (session.status === 'completed') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-success">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Completed
      </span>
    );
  }
  if (cancelled) {
    return <span className="shrink-0 text-xs text-destructive">Cancelled</span>;
  }
  if (session.status === 'terminated') {
    return <span className="shrink-0 text-xs text-destructive">Terminated</span>;
  }
  if (session.status === 'expired' || isSessionExpired(session)) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-orange-500/30 bg-orange-500/10 px-2.5 py-1 text-xs font-medium text-orange-700 dark:text-orange-400">
        Link expired
      </span>
    );
  }
  // Scheduled but not open yet — say when, rather than a bare "Unavailable"
  // that tells the candidate nothing and makes the team field questions.
  if (session.status === 'scheduled' && isSessionNotYetOpen(session)) {
    const countdown = formatCountdown(session.linkOpensAt);
    return (
      <span
        title={`You can start from ${formatWhen(session.linkOpensAt)}`}
        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-xs font-medium text-sky-700 dark:text-sky-400"
      >
        <Clock className="h-3 w-3" />
        {countdown ? `Opens ${countdown}` : 'Opens soon'}
      </span>
    );
  }

  return (
    <span className="shrink-0 text-xs text-muted-foreground">Unavailable</span>
  );
}

function hasSessionDetails(session) {
  const t = session.template;
  if (!t) return false;
  return Boolean(
    t.jobDescription ||
    t.description ||
    t.department ||
    t.difficultyLevel ||
    session.interviewer
  );
}

function SessionRow({ session, expanded, onToggleExpand, onStart, isLast }) {
  const status = resolveSessionStatus(session);
  const isExpanded = expanded === session._id;
  const ready = canStartInterview(session);
  const duration = formatDuration(session.durationMinutes || session.template?.durationMinutes);
  const questions = session.numberOfQuestions || session.template?.numberOfQuestions;
  const showDetails = hasSessionDetails(session);

  const metaParts = [
    session.companyName,
    session.template?.position,
    formatScheduledOn(session.scheduledAt),
    duration,
    questions ? `${questions} questions` : null,
  ].filter(Boolean);

  return (
    <div className={cn(!isLast && 'border-b border-border/70')}>
      <div
        className={cn(
          'group flex min-h-[3.25rem] items-center gap-2 px-3 py-2.5 transition-colors sm:gap-3 sm:px-4',
          ready && 'bg-primary/[0.03]',
          'hover:bg-muted/30'
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2 flex-wrap">
            <p className="truncate text-sm font-semibold text-foreground">
              {session.template?.title || 'Interview'}
            </p>
            {session.roundName && (
              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                {session.roundName}
              </span>
            )}
            <StatusBadge status={status} className="shrink-0 scale-90" />
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{metaParts.join(' · ')}</p>

          {/* Both edges of the link window, in plain words. The candidate used
              to see only "Unavailable" and had to ask someone. */}
          {status === 'scheduled' && session.linkOpensAt && session.linkClosesAt && (
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
              <span className={cn('inline-flex items-center gap-1',
                isSessionNotYetOpen(session) ? 'font-medium text-sky-700 dark:text-sky-400' : 'text-muted-foreground')}>
                <PlayCircle className="h-3 w-3" aria-hidden />
                {isSessionNotYetOpen(session)
                  ? `You can start from ${formatWhen(session.linkOpensAt)}`
                  : 'You can start now'}
              </span>
              <span className="text-muted-foreground/50" aria-hidden>·</span>
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3 w-3" aria-hidden />
                Link valid until {formatWhen(session.linkClosesAt)}
              </span>
            </p>
          )}
        </div>

        <div className="hidden shrink-0 items-center gap-3 text-xs text-muted-foreground lg:flex">
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden />
            {formatScheduledOn(session.scheduledAt)}
            {relativeDayHint(session.scheduledAt) && (
              <span className="text-muted-foreground/70">({relativeDayHint(session.scheduledAt)})</span>
            )}
          </span>
          {duration && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" aria-hidden />
              {duration}
            </span>
          )}
        </div>

        <SessionAction session={session} onStart={onStart} />

        {showDetails && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 shrink-0 gap-1 px-2.5 text-xs"
            onClick={() => onToggleExpand(session._id)}
            aria-expanded={isExpanded}
          >
            {isExpanded ? 'Hide' : 'Details'}
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {session.status === 'cancelled' && (
        <p className="border-t border-destructive/20 bg-destructive/5 px-4 py-2 text-xs text-destructive">
          This interview was cancelled. Contact the hiring team for help.
        </p>
      )}

      {isExpanded && showDetails && (
        <div className="border-t border-border/60 bg-muted/20 px-4 py-3">
          <div className="space-y-3">
            {session.template?.jobDescription ? (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <FileText className="h-3 w-3" aria-hidden />
                  Job description
                </p>
                <div className="max-h-48 overflow-y-auto rounded-md border border-border/60 bg-background/80 p-3">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                    {session.template.jobDescription}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No job description provided for this role.</p>
            )}
            {session.template?.description && (
              <div>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Instructions
                </p>
                <div className="max-h-32 overflow-y-auto rounded-md border border-border/60 bg-background/80 p-3">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                    {session.template.description}
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-3 text-xs">
              {session.template?.department && (
                <span>
                  <span className="text-muted-foreground">Dept </span>
                  <span className="font-medium">{session.template.department}</span>
                </span>
              )}
              {session.template?.difficultyLevel && (
                <span>
                  <span className="text-muted-foreground">Level </span>
                  <span className="font-medium capitalize">{session.template.difficultyLevel}</span>
                </span>
              )}
              {session.interviewer && (
                <span className="inline-flex items-center gap-1">
                  <User className="h-3 w-3 text-muted-foreground" aria-hidden />
                  <span className="font-medium">
                    {session.interviewer.firstName} {session.interviewer.lastName}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-14 w-full rounded-md" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-14 rounded-md" />
        ))}
      </div>
      <Skeleton className="h-64 w-full rounded-md" />
    </div>
  );
}

function CandidateDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSession, setExpandedSession] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('interviews'); // 'interviews' | 'support'
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const PAGE_SIZE = 10;

  // Support state
  const [tickets, setTickets]           = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [showRaiseModal, setShowRaiseModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchSessions = async (isRefresh = false) => {
      try {
        const response = await apiClient.getMySessions(page, PAGE_SIZE);
        if (cancelled) return;
        setSessions(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalRecords(response.pagination?.totalRecords ?? response.data?.length ?? 0);
      } catch {
        // A background refresh failing must not replace a working list with an
        // error the candidate cannot act on.
        if (!cancelled && !isRefresh) setError('Could not fetch your scheduled interviews.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSessions();

    // Fetched once on mount, this page showed a stale time indefinitely after
    // HR rescheduled — the candidate had no reason to reload. Refresh when the
    // tab is looked at again, and slowly while it sits open, so the countdown
    // and any reschedule stay honest.
    const onFocus = () => { if (document.visibilityState === 'visible') fetchSessions(true); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    const timer = setInterval(() => fetchSessions(true), 60_000);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
      clearInterval(timer);
    };
  }, [page]);

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await apiClient.getSupportTickets({ type: 'candidate_to_company' });
      setTickets(res.data || []);
    } catch {
      // non-fatal
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'support') fetchTickets();
  }, [activeTab]);

  const stats = useMemo(() => {
    let ready = 0;
    let completed = 0;
    let closed = 0;
    for (const s of sessions) {
      const bucket = getFilterBucket(s);
      if (bucket === 'ready') ready += 1;
      else if (bucket === 'completed') completed += 1;
      else closed += 1;
    }
    return { total: sessions.length, ready, completed, closed };
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    if (statusFilter === 'all') return sessions;
    return sessions.filter((s) => getFilterBucket(s) === statusFilter);
  }, [sessions, statusFilter]);

  useEffect(() => { setPage(1); }, [statusFilter, activeTab]);

  const handleToggleExpand = (sessionId) => {
    setExpandedSession((prev) => (prev === sessionId ? null : sessionId));
  };

  const handleStart = (link) => navigate(`/instructions/${link}`);

  const firstName = user?.firstName || 'there';
  const readyCount = stats.ready;

  return (
    <CandidateShell>
      <div className="mx-auto w-full max-w-none space-y-4">
        <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border/60 pb-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
              Welcome, {firstName}
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {readyCount > 0
                ? `${readyCount} interview${readyCount > 1 ? 's' : ''} ready to start`
                : 'Your scheduled AI interviews'}
            </p>
          </div>
          <p className="hidden text-xs text-muted-foreground sm:block">
            <FileText className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden />
            Expand a row for job details · Start when your session is active
          </p>
        </header>

        {/* Main tab switcher */}
        <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-1 w-fit">
          <button
            onClick={() => setActiveTab('interviews')}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
              activeTab === 'interviews'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Calendar className="h-3.5 w-3.5" />
            Interviews
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
              activeTab === 'support'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <HelpCircle className="h-3.5 w-3.5" />
            Support
            {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length > 0 && (
              <span className="rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-primary-foreground">
                {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}
              </span>
            )}
          </button>
        </div>

        {/* ── Interviews tab ── */}
        {activeTab === 'interviews' && (
          <>
            {isLoading && <DashboardSkeleton />}
            {error && (
              <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            {!isLoading && !error && sessions.length === 0 && (
              <EmptyState icon={Calendar} title="No interviews scheduled" description="When a session is scheduled for you, it will appear here." />
            )}
            {!isLoading && sessions.length > 0 && (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <DashboardKpiCard compact title="Total" value={stats.total} icon={Briefcase} tone="default" onClick={() => setStatusFilter('all')} />
                  <DashboardKpiCard compact title="Ready" value={stats.ready} icon={PlayCircle} tone="primary" onClick={() => setStatusFilter('ready')} />
                  <DashboardKpiCard compact title="Completed" value={stats.completed} icon={CheckCircle2} tone="success" onClick={() => setStatusFilter('completed')} />
                  <DashboardKpiCard compact title="Closed" value={stats.closed} icon={Calendar} tone="default" onClick={() => setStatusFilter('closed')} />
                </div>
                <section aria-labelledby="sessions-heading">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h2 id="sessions-heading" className="text-sm font-semibold">Interviews</h2>
                      <p className="text-[11px] text-muted-foreground">
                        {filteredSessions.length} shown • {totalRecords} total • Page {page} of {totalPages}
                      </p>
                    </div>
                    <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                      <TabsList className="h-8 p-0.5">
                        {FILTER_TABS.map((t) => (
                          <TabsTrigger key={t.value} value={t.value} className="h-7 px-2.5 text-xs">{t.label}</TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>
                  </div>
                  {filteredSessions.length === 0 ? (
                    <EmptyState icon={Calendar} title="No interviews in this filter" description="Try another tab." actionLabel="Show all" onAction={() => setStatusFilter('all')} />
                  ) : (
                    <>
                      <div className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
                        {filteredSessions.map((session, index) => (
                          <SessionRow key={session._id} session={session} expanded={expandedSession} onToggleExpand={handleToggleExpand} onStart={handleStart} isLast={index === filteredSessions.length - 1} />
                        ))}
                      </div>
                      <div className="mt-2">
                        <Pagination
                          page={page}
                          totalPages={totalPages}
                          totalItems={totalRecords}
                          pageSize={PAGE_SIZE}
                          onPageChange={setPage}
                        />
                      </div>
                    </>
                  )}
                </section>
              </>
            )}
          </>
        )}

        {/* ── Support tab ── */}
        {activeTab === 'support' && (
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold">My Support Tickets</h2>
                <p className="text-[11px] text-muted-foreground">Issues raised to your company's recruitment team</p>
              </div>
              <Button size="sm" onClick={() => setShowRaiseModal(true)} className="gap-1.5 h-8 text-xs">
                <Plus className="h-3.5 w-3.5" />
                Raise Ticket
              </Button>
            </div>

            {ticketsLoading && (
              <div className="space-y-2">
                {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full rounded-md" />)}
              </div>
            )}

            {!ticketsLoading && tickets.length === 0 && (
              <EmptyState
                icon={HelpCircle}
                title="No support tickets yet"
                description="Having trouble with an interview? Raise a ticket and the company team will help you."
                actionLabel="Raise a Ticket"
                onAction={() => setShowRaiseModal(true)}
              />
            )}

            {!ticketsLoading && tickets.length > 0 && (
              <div className="overflow-hidden rounded-md border border-border bg-card shadow-sm divide-y divide-border">
                {tickets.map(ticket => (
                  <button
                    key={ticket._id}
                    onClick={() => setSelectedTicketId(ticket._id)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-start gap-3"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 mt-0.5">
                      <MessageSquare className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold', TICKET_STATUS_STYLES[ticket.status] || TICKET_STATUS_STYLES.open)}>
                          {TICKET_STATUS_LABELS[ticket.status] || ticket.status}
                        </span>
                        <span className="text-[10px] text-muted-foreground font-mono">{ticket.ticketNumber}</span>
                      </div>
                      <p className="mt-0.5 text-sm font-medium text-foreground truncate">{ticket.subject}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {ticket.sessionTitle && <><Briefcase className="inline h-3 w-3 mr-0.5" />{ticket.sessionTitle} · </>}
                        {ticket.lastActivityAt ? new Date(ticket.lastActivityAt).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-2" />
                  </button>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Modals */}
      <RaiseTicketModal
        open={showRaiseModal}
        onClose={() => setShowRaiseModal(false)}
        sessions={sessions}
        onCreated={fetchTickets}
      />
      <CandidateTicketThread
        ticketId={selectedTicketId}
        open={!!selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        currentUserId={user?._id}
      />
    </CandidateShell>
  );
}

export default CandidateDashboard;
